/**
 * OfficialTimeSelector (GDN-2510)
 *
 * The official session-time selector — which clock stamps the note & claim.
 * One model (in room / scheduled / manual — see officialTimeModel.ts), two
 * presentations:
 *
 *   - variant="dropdown" (recording screen): one compact line
 *     "Time on note & claim: {source} · {times} · {date}" opening a menu of
 *     the three sources with resolved values. A dictation with nothing chosen
 *     renders the amber ASK state ("Set the time for note & claim") — the
 *     control visibly wants input but never blocks Generate Note.
 *   - variant="inline" (note popover): a collapsed readout
 *     "Session time · {source} · {times}" with a Change affordance that
 *     expands into an inline radio selector. Time-of-day only — the Date of
 *     Service field above the selector owns the billing date. Locks (readout
 *     only) once the note is signed.
 *
 * Controlled writes: the component never mutates. `onCommit` fires with an
 * OfficialTimePatch (start_time / end_time / date_of_service / time_source
 * ONLY — never scheduled_for) at real decision points; the parent persists.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Event as EventIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  FormControlLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { ManualTimeEditor } from './ManualTimeEditor';
import {
  buildInRoomPatch,
  buildManualPatch,
  buildScheduledPatch,
  computeEndClock,
  formatClock,
  formatDateShort,
  formatResolvedTimes,
  getEffectiveSource,
  type ManualTimeValue,
  type OfficialTimeEncounterLike,
  type OfficialTimePatch,
  type OfficialTimeSource,
  type RecordingWindow,
  resolveInRoomTimes,
  resolveRecordingTimes,
  resolveScheduledTimes,
  seedManualValue,
  toLocalDateString,
} from './officialTimeModel';

export interface OfficialTimeSelectorProps {
  variant: 'dropdown' | 'inline';
  encounter: OfficialTimeEncounterLike;
  /**
   * The id of the encounter this selector is bound to. When it changes, the
   * dropdown resets its local source/manual selection — the recording screen
   * does NOT remount on client-side encounter→encounter navigation, so without
   * this a stale selection from a previously-viewed encounter could be
   * committed onto a different encounter's claim.
   */
  encounterId?: string | null;
  /**
   * Every saved recording is a dictation (< 120s, isFullEncounterRecording):
   * the recording clock is the dictation moment, not the visit time.
   */
  isDictation: boolean;
  /**
   * Fired with the write payload at a real decision point; parent persists.
   * Return (or resolve) `false` when the write fails so the selector can roll
   * back the optimistic selection — a value that never saved must not keep
   * showing as the chosen source (it would end up implying it reached the claim).
   */
  onCommit: (patch: OfficialTimePatch) => void | Promise<boolean>;
  /**
   * The true recording moment, from recording metadata (live recorder state,
   * the recording document(s), or the note's transcription timestamps). The
   * "Time of dictation" / "In room" row resolves from THIS — never from the
   * encounter clock once a source has written the clock (the clock is the
   * selector's output; reading it back would echo the last applied source).
   * Without it the row survives only while time_source is null/'in_room'.
   */
  recordingWindow?: RecordingWindow | null;
  disabled?: boolean;
  /**
   * Dropdown only: the dictation-with-appointment scheduled default was just
   * auto-applied — show the one-line hint under the control.
   */
  showDefaultedHint?: boolean;
  /** Inline only: the note is signed — readout only, no Change. */
  locked?: boolean;
}

const DEFAULTED_HINT =
  'Defaulted to the scheduled visit time — change it if the visit ran differently.';

const INLINE_CAPTION =
  'Sets the time of day only — the Date of Service above is the billing date.';

interface SourceOption {
  source: OfficialTimeSource;
  label: string;
  shortLabel: string;
  /** "1:29–1:36 PM" (+ " · Mar 24" in the dropdown). Null for manual. */
  resolved: string | null;
  disabledReason?: string;
  deEmphasized?: boolean;
  annotation?: string;
}

/**
 * The three source rows for the current encounter. In the dropdown the
 * resolved values carry the date; the inline (DOS-split) variant is
 * time-of-day only. For a dictation the recording-clock row is labeled
 * "Time of dictation", de-emphasized, and annotated — it is almost never the
 * visit time.
 *
 * The recording row resolves from resolveRecordingTimes (metadata first; the
 * clock only while it still is the recording clock). When neither yields an
 * honest value the row is omitted — a row claiming "recording time" with the
 * last-applied source's values would be worse than no row.
 */
const buildOptions = (
  encounter: OfficialTimeEncounterLike,
  isDictation: boolean,
  withDates: boolean,
  recordingWindow?: RecordingWindow | null,
): SourceOption[] => {
  const inRoomTimes = resolveRecordingTimes(encounter, recordingWindow);
  const scheduledTimes = resolveScheduledTimes(encounter);

  const resolve = (times: ReturnType<typeof resolveInRoomTimes>): string | null => {
    const clockPart = formatResolvedTimes(times);
    if (!clockPart || !times) return null;
    return withDates
      ? `${clockPart} · ${formatDateShort(toLocalDateString(times.start))}`
      : clockPart;
  };

  const inRoomOption: SourceOption | null = inRoomTimes
    ? {
        source: 'in_room',
        label: isDictation ? 'Time of dictation' : withDates ? 'Time in room' : 'In room',
        shortLabel: isDictation ? 'Dictated' : 'In room',
        resolved: resolve(inRoomTimes),
        deEmphasized: isDictation,
        annotation: isDictation ? 'recording time' : undefined,
      }
    : null;

  // GDN-3909: the slot may belong to a DIFFERENT day than the recording — the
  // encounter is linked to the wrong appointment (yesterday's slot, a
  // pre-created future one). Say so on the row rather than letting "Scheduled"
  // read as this visit: carry the date in the label and mute the row, the same
  // treatment the dictation clock gets. Both days must be known to claim a
  // mismatch; an unknowable recording day leaves the row unchanged.
  const scheduledDay = scheduledTimes ? toLocalDateString(scheduledTimes.start) : null;
  const recordingDay = inRoomTimes ? toLocalDateString(inRoomTimes.start) : null;
  const scheduledIsOtherDay = Boolean(
    scheduledDay && recordingDay && scheduledDay !== recordingDay,
  );

  const scheduledOption: SourceOption = {
    source: 'scheduled',
    label:
      scheduledIsOtherDay && scheduledDay
        ? `Scheduled · ${formatDateShort(scheduledDay)}`
        : 'Scheduled',
    shortLabel: 'Scheduled',
    resolved: resolve(scheduledTimes),
    disabledReason: scheduledTimes ? undefined : 'No appointment linked',
    deEmphasized: scheduledIsOtherDay,
    annotation: scheduledIsOtherDay ? 'another day' : undefined,
  };

  const manualOption: SourceOption = {
    source: 'manual',
    label: 'Set manually',
    shortLabel: 'Manual',
    resolved: null,
  };

  // Dictation: the scheduled slot is the best guess, so it leads; the
  // recording clock trails, muted. Full recording: the clock leads.
  // A slot on another day is NOT the best guess — it drops behind the
  // recording clock so the muted row never sits in the lead position.
  if (isDictation) {
    return scheduledIsOtherDay
      ? [...(inRoomOption ? [inRoomOption] : []), scheduledOption, manualOption]
      : [scheduledOption, ...(inRoomOption ? [inRoomOption] : []), manualOption];
  }
  return [...(inRoomOption ? [inRoomOption] : []), scheduledOption, manualOption];
};

/** "1:30–2:00 PM · May 19" for the closed manual chip; honest about gaps. */
const compactManual = (manual: ManualTimeValue): string => {
  const datePart = manual.date ? formatDateShort(manual.date) : '';
  if (!manual.start) return datePart ? `${datePart} · time not set` : 'time not set';
  const end = computeEndClock(manual.start, manual.durationMin);
  const startClock = formatClock(manual.start);
  const clockPart = end ? `${startClock.replace(/ (AM|PM)$/, '')}–${end}` : startClock;
  return datePart ? `${clockPart} · ${datePart}` : clockPart;
};

// ---------------------------------------------------------------------------
// Dropdown variant — recording screen.
// ---------------------------------------------------------------------------

const DropdownSelector: React.FC<Omit<OfficialTimeSelectorProps, 'variant'>> = ({
  encounter,
  encounterId,
  isDictation,
  onCommit,
  recordingWindow,
  disabled = false,
  showDefaultedHint = false,
}) => {
  const theme = useTheme();
  const [localSource, setLocalSource] = useState<OfficialTimeSource | null>(null);
  const [manual, setManual] = useState<ManualTimeValue>(() => seedManualValue(encounter));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Reset the per-encounter local selection when the bound encounter changes.
  // The parent does not remount across client-side encounter navigation, so a
  // stale source/manual value must never survive onto a different encounter's
  // write (patient-safety: prevents committing encounter A's time onto B).
  // Keyed on identity only — not on `encounter` data — so an unrelated field
  // refetch on the SAME encounter does not wipe an in-progress selection.
  useEffect(() => {
    setLocalSource(null);
    setManual(seedManualValue(encounter));
    setAnchorEl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounterId]);

  const options = useMemo(
    () => buildOptions(encounter, isDictation, true, recordingWindow),
    [encounter, isDictation, recordingWindow],
  );

  const displaySource =
    localSource ?? getEffectiveSource(encounter, isDictation, { recordingWindow });
  const active = displaySource ? options.find(o => o.source === displaySource) : undefined;
  const isAsk = displaySource === null;

  const closedText = (() => {
    if (isAsk) return 'Set the time for note & claim';
    if (displaySource === 'manual') return `Manual · ${compactManual(manual)}`;
    return active ? `${active.shortLabel} · ${active.resolved ?? '—'}` : '—';
  })();

  const handleSelect = (source: OfficialTimeSource) => {
    setAnchorEl(null);
    if (source === 'manual') {
      setLocalSource('manual');
      setManual(seedManualValue(encounter));
      return;
    }
    const patch =
      source === 'in_room'
        ? buildInRoomPatch(encounter, { includeDateOfService: true, recordingWindow })
        : buildScheduledPatch(encounter, { includeDateOfService: true });
    if (!patch) return;
    setLocalSource(source);
    commitWithRollback(patch);
  };

  const handleManualCommit = (value: ManualTimeValue) => {
    const patch = buildManualPatch(value, { includeDateOfService: true });
    if (patch) commitWithRollback(patch);
  };

  // A rejected write resolves false — drop the optimistic local selection so
  // the readout reflects what's actually persisted (getEffectiveSource), never
  // a value that never reached the claim. The identity-keyed reset survives an
  // unrelated refetch, so without this a failed PATCH would display forever.
  const commitWithRollback = (patch: OfficialTimePatch) => {
    void Promise.resolve(onCommit(patch))
      .then(saved => {
        if (saved === false) setLocalSource(null);
      })
      .catch(() => setLocalSource(null));
  };

  // ASK state: amber attention treatment — wants input, but nothing blocks.
  const askSx = {
    backgroundColor: alpha(theme.palette.warning.main, 0.12),
    border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
    '&:hover': {
      backgroundColor: alpha(theme.palette.warning.main, 0.18),
      borderColor: alpha(theme.palette.warning.main, 0.7),
    },
  };
  const restingSx = {
    backgroundColor: alpha(theme.palette.grey[500], 0.1),
    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
    '&:hover': {
      backgroundColor: alpha(theme.palette.grey[500], 0.15),
      borderColor: alpha(theme.palette.grey[500], 0.3),
    },
  };

  return (
    <Box sx={{ width: '100%' }} data-testid="official-time-selector">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
          Time on note &amp; claim:
        </Typography>
        <Box
          role="button"
          aria-label="Time on note and claim"
          onClick={e => {
            if (!disabled) setAnchorEl(e.currentTarget);
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            px: 1,
            py: 0.75,
            borderRadius: 2,
            cursor: disabled ? 'default' : 'pointer',
            ...(disabled ? { opacity: 0.5 } : {}),
            ...(isAsk ? askSx : restingSx),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            {isAsk && <EventIcon sx={{ fontSize: 15, color: 'warning.dark', flexShrink: 0 }} />}
            <Typography
              variant="caption"
              sx={{
                fontWeight: isAsk ? 600 : 500,
                color: isAsk ? 'warning.dark' : 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {closedText}
            </Typography>
          </Box>
          <ArrowDropDownIcon
            sx={{
              fontSize: 16,
              color: isAsk ? 'warning.dark' : 'text.secondary',
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>

      {/* One-line hint when the scheduled default was applied silently */}
      {showDefaultedHint && displaySource === 'scheduled' && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', mt: 0.5, pl: 0.25 }}
        >
          {DEFAULTED_HINT}
        </Typography>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        // In the ASK state nothing is chosen — don't let the first item's
        // autofocus highlight read as a silent selection.
        disableAutoFocusItem
      >
        {options.map(option => (
          <MenuItem
            key={option.source}
            selected={option.source === displaySource}
            disabled={Boolean(option.disabledReason)}
            onClick={() => handleSelect(option.source)}
            sx={{
              minWidth: 300,
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              ...(option.deEmphasized ? { opacity: 0.55 } : {}),
            }}
          >
            <Typography variant="body2">{option.label}</Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {option.disabledReason ??
                  option.resolved ??
                  (option.source === 'manual' ? 'date + start + duration' : '')}
              </Typography>
              {option.annotation && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'block' }}
                >
                  {option.annotation}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {displaySource === 'manual' && (
        <ManualTimeEditor
          value={manual}
          onChange={setManual}
          onCommit={handleManualCommit}
          showDateField
          disabled={disabled}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Inline variant — note popover (DOS-split: time of day only).
// ---------------------------------------------------------------------------

/**
 * Factual readout for the collapsed state. A stored time_source names the
 * choice; with none stored the readout reports what exists (clock → in-room
 * times, else the scheduled slot) without implying a decision.
 */
const getInlineReadout = (
  encounter: OfficialTimeEncounterLike,
  isDictation: boolean,
  recordingWindow?: RecordingWindow | null,
): { label: string; resolved: string } => {
  const source = getEffectiveSource(encounter, isDictation, { recordingWindow });
  // The official clock — correct readout for manual (the clock holds the
  // manually entered times).
  const clock = formatResolvedTimes(resolveInRoomTimes(encounter));
  // The recording moment — metadata first, clock only while untouched.
  const recording = formatResolvedTimes(resolveRecordingTimes(encounter, recordingWindow));
  const scheduled = formatResolvedTimes(resolveScheduledTimes(encounter));

  if (source === 'manual' && clock) return { label: 'Manual', resolved: clock };
  if (source === 'scheduled' && scheduled) return { label: 'Scheduled', resolved: scheduled };
  if (source === 'in_room' && recording) {
    return { label: isDictation ? 'Dictated' : 'In room', resolved: recording };
  }
  // Nothing stored (legacy or ask): report the facts.
  if (recording) return { label: isDictation ? 'Dictated' : 'In room', resolved: recording };
  if (scheduled) return { label: 'Scheduled', resolved: scheduled };
  return { label: 'Not set', resolved: '—' };
};

const InlineSelector: React.FC<Omit<OfficialTimeSelectorProps, 'variant'>> = ({
  encounter,
  encounterId,
  isDictation,
  onCommit,
  recordingWindow,
  disabled = false,
  locked = false,
}) => {

  const [open, setOpen] = useState(false);
  const [localSource, setLocalSource] = useState<OfficialTimeSource | null>(null);
  const [manual, setManual] = useState<ManualTimeValue>(() => seedManualValue(encounter));

  // Reset the per-encounter local selection when the bound encounter changes —
  // symmetric with DropdownSelector. The note popover can persist across
  // client-side note→note navigation (NoteChatView is not remounted on
  // noteId), so a stale source/manual value must never survive onto a
  // different encounter's write. Keyed on identity only.
  useEffect(() => {
    setOpen(false);
    setLocalSource(null);
    setManual(seedManualValue(encounter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounterId]);

  const options = useMemo(
    () => buildOptions(encounter, isDictation, false, recordingWindow),
    [encounter, isDictation, recordingWindow],
  );

  const displaySource =
    localSource ?? getEffectiveSource(encounter, isDictation, { recordingWindow });
  const readout = getInlineReadout(encounter, isDictation, recordingWindow);

  // The manual editor anchors its start_time to the billing day — the DOS
  // field owns the date, so the selector never writes date_of_service here.
  // resolveInRoomTimes (not a bare `new Date`) so a suffix-less server
  // timestamp is read as UTC, not local wall time.
  const clockStart = resolveInRoomTimes(encounter)?.start;
  // Match the day shown in the note popover's Date of Service field
  // (NoteChatView: service_date ?? date_of_service) so a manual session-time
  // anchors to the same billing day the provider sees — the two must not
  // diverge. service_date is the resolver's canonical day; when date_of_service
  // is set the resolver returns it, so they normally agree, but pin precedence.
  const anchorDate =
    encounter.service_date ??
    encounter.date_of_service ??
    (clockStart ? toLocalDateString(clockStart) : undefined);

  const handleSelect = (source: OfficialTimeSource) => {
    if (source === 'manual') {
      setLocalSource('manual');
      setManual(seedManualValue(encounter));
      return;
    }
    const patch =
      source === 'in_room'
        ? buildInRoomPatch(encounter, { includeDateOfService: false, recordingWindow })
        : buildScheduledPatch(encounter, { includeDateOfService: false });
    if (!patch) return;
    setLocalSource(source);
    commitWithRollback(patch);
  };

  const handleManualCommit = (value: ManualTimeValue) => {
    const patch = buildManualPatch(value, { includeDateOfService: false, anchorDate });
    if (patch) commitWithRollback(patch);
  };

  // Roll back the optimistic selection when the write fails (see DropdownSelector).
  const commitWithRollback = (patch: OfficialTimePatch) => {
    void Promise.resolve(onCommit(patch))
      .then(saved => {
        if (saved === false) setLocalSource(null);
      })
      .catch(() => setLocalSource(null));
  };

  if (open && !locked) {
    return (
      <Box
        data-testid="official-time-selector"
        sx={{ px: 1.5, py: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          Session time
        </Typography>
        <RadioGroup
          value={displaySource ?? ''}
          onChange={e => handleSelect(e.target.value as OfficialTimeSource)}
        >
          {options.map(option => (
            <FormControlLabel
              key={option.source}
              value={option.source}
              control={<Radio size="small" />}
              disabled={disabled || Boolean(option.disabledReason)}
              sx={{
                mr: 0,
                width: '100%',
                '& .MuiFormControlLabel-label': { width: '100%' },
                ...(option.deEmphasized ? { opacity: 0.6 } : {}),
              }}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.label}
                  </Typography>
                  <Box sx={{ textAlign: 'right', minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: option.disabledReason ? 'text.disabled' : 'text.secondary' }}
                    >
                      {option.disabledReason ??
                        option.resolved ??
                        (option.source === 'manual' ? 'start + duration' : '')}
                    </Typography>
                    {option.annotation && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'block' }}
                      >
                        {option.annotation}
                      </Typography>
                    )}
                  </Box>
                </Box>
              }
            />
          ))}
        </RadioGroup>
        {/* Helper — expanded state only */}
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          {INLINE_CAPTION}
        </Typography>
        {displaySource === 'manual' && (
          <ManualTimeEditor
            value={manual}
            onChange={setManual}
            onCommit={handleManualCommit}
            showDateField={false}
            disabled={disabled}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      data-testid="official-time-selector"
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        ...(locked ? { backgroundColor: 'action.hover' } : {}),
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          Session time
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: locked ? 'text.secondary' : 'text.primary' }}
        >
          {readout.label} · {readout.resolved}
        </Typography>
      </Box>
      {locked ? (
        <Tooltip title="Locked — this note is signed" arrow>
          <LockIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
        </Tooltip>
      ) : (
        <Button size="small" disabled={disabled} onClick={() => setOpen(true)} sx={{ flexShrink: 0 }}>
          Change
        </Button>
      )}
    </Box>
  );
};

export const OfficialTimeSelector: React.FC<OfficialTimeSelectorProps> = ({
  variant,
  ...rest
}) => (variant === 'dropdown' ? <DropdownSelector {...rest} /> : <InlineSelector {...rest} />);
